import { useState } from 'react';

interface BrandLogoProps {
  /** The logo's URL. Anything that is not an http(s) URL is treated as absent. */
  src?: string | null;
  /** Brand name, for the image's alt text. Omit for a purely decorative tile. */
  name?: string;
  /** Edge length of the square tile in px. Defaults to the 64px proposals card. */
  size?: number;
  /**
   * The brand's `logoHeroTransparent` flag: its logo is drawn to sit on dark
   * artwork — typically white or very light — and would vanish on a white tile,
   * so the tile goes grey.
   */
  lightArtwork?: boolean;
  /** Defer loading, for logos inside long lists. */
  lazy?: boolean;
  className?: string;
}

/**
 * A brand's logo in a square, bordered tile — or a dashed empty tile of the
 * same size when there is no logo (or it fails to load), so rows and headers
 * keep their alignment either way.
 *
 * One component for every place the admin shows a brand logo at a glance: the
 * Microbrand Atlas proposals list and detail card, and the per-brand references
 * page header.
 */
export function BrandLogo({
  src,
  name,
  size = 64,
  lightArtwork = false,
  lazy = false,
  className = '',
}: BrandLogoProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const url = typeof src === 'string' && /^https?:\/\//i.test(src.trim()) ? src.trim() : '';
  const box = { width: size, height: size };

  if (!url || failedSrc === url) {
    return (
      <div
        data-testid="brand-logo-placeholder"
        aria-hidden="true"
        style={box}
        className={`flex-none rounded border border-dashed border-gray-200 ${className}`}
      />
    );
  }
  return (
    <img
      src={url}
      alt={name ? `${name} logo` : ''}
      loading={lazy ? 'lazy' : undefined}
      referrerPolicy="no-referrer"
      onError={() => setFailedSrc(url)}
      style={box}
      className={`flex-none rounded border border-gray-200 object-contain ${
        lightArtwork ? 'bg-gray-500' : 'bg-white'
      } ${className}`}
    />
  );
}
